<template>
  <v-container class="mt-12 overview-container">
    <div v-if="!loading">
      <v-row v-if="showUpdatesBanner" class="revise-banner">
        <v-col cols="12" class="revise-banner-col">
          <div class="content">
            <div class="left">
              <v-icon color="white" class="completed-tick">{{
                icons.mdiAlertCircleOutline
              }}</v-icon>
            </div>
            <div class="center">
              <div>
                <strong class="title">{{ reviseBannerTitle }}</strong>
              </div>
              <div>
                <strong class="text">{{ reviseBannerText }}</strong>
              </div>
            </div>
            <div class="right">
              <v-icon @click="closeReviseBanner" color="white" class="">{{
                icons.mdiClose
              }}</v-icon>
            </div>
          </div>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" xl="5" lg="5" md="6" class="pa-4">
          <div class="mx-auto" v-if="whitelabel.brand === 'quickwill'">
            <div v-if="!showPreExistingWillForm">
              <div v-if="coBrandedLogoUrl" class="co-branded-logo-container">
                <img
                  :src="coBrandedLogoUrl"
                  alt="Logo"
                  title="Logo"
                  class="menubar-logo internal-main-logo"
                />
              </div>

              <div class="welcome-heading-container">
                <h1 class="welcome-heading">
                  Welcome to {{ whitelabel.overviewPageTitle }}
                </h1>
                <h1 class="partnership-title">
                  {{ whitelabel.overviewPartnershipTitle }}
                </h1>
              </div>

              <OverviewProgressBars
                :testator="testator"
                :steps="steps"
                :loading="stepsLoading"
                :willDetailsChanged.sync="willDetailsChanged"
                :previewWillDetailsChanged.sync="previewWillDetailsChanged"
                @previewWillDownloaded="previewWillDownloaded"
                @willDownloaded="willDownloaded"
              />
              <OverviewWillVerification
                v-if="testator.willVersions.length"
                class="mt-8"
                :testator="testator"
                :will-details-changed="willDetailsChanged"
                @booking-success="refetchTestator"
              />
            </div>

            <PreExistingWillForm
              v-else
              :testator="testator"
              :people-relations="peopleRelations"
              @hideForm="showPreExistingWillForm = false"
            />
          </div>
          <div v-else>
            <div class="welcome-heading-container">
              <h1 class="welcome-heading">
                Welcome to {{ whitelabel.overviewPageTitle }}
              </h1>
              <h1 class="partnership-title">
                {{ whitelabel.overviewPartnershipTitle }}
              </h1>
            </div>

            <LeftPanelContainer
              v-if="determineLeftPanelContainer"
              :testator="testator"
              :numberOfCompletedSteps="numberOfCompletedSteps"
              :totalSteps="numberOfSteps"
              :allStepsCompleted="allStepsCompleted"
              :willDetailsChanged.sync="willDetailsChanged"
              :previewWillDetailsChanged.sync="previewWillDetailsChanged"
              @previewWillDownloaded="previewWillDownloaded"
              @willDownloaded="willDownloaded"
            />
          </div>
        </v-col>

        <v-col
          cols="12"
          xl="7"
          lg="7"
          md="6"
          class="d-flex flex-column align-center"
        >
          <div
            class="d-flex flex-column"
            :class="{
              'steps-title-container': !isMobile,
              'steps-title-container-mobile': isMobile,
            }"
          >
            <h2 class="steps-title">Create your will in a few easy steps</h2>
            <p class="mt-2 mb-4 steps-description">
              Follow these simple steps below.
            </p>
          </div>
          <OverviewCard
            v-if="referAFriendEnalbed && allStepsCompleted && isTestator"
            :step="referralStepObject"
            :loading="false"
          />

          <OverviewCard
            v-for="step in steps"
            :key="step.stepNumber"
            :step="step"
            :loading="stepsLoading"
          />
        </v-col>
      </v-row>
    </div>

    <div v-else class="text-center">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <SuccessSnackbar v-model="showAlert" :message="alertMessage" />
    <SuccessSnackbar
      v-model="insuranceAlert"
      :message="insuranceAlertMessage"
      :style="{ 'margin-top': calcInsuranceMargin }"
    />
    <AdvisorCodeDialog
      v-if="showAdvisorCodeModal"
      :value="showAdvisorCodeModal"
      @advisorCodeModalClosed="advisorCodeModalClosed"
    ></AdvisorCodeDialog>
    <TebaEmailDialog
      v-if="showTebaEmailModal"
      :value="showTebaEmailModal"
      @tebaEmailModalClosed="tebaEmailModalClosed"
      @emailSaved="tebaEmailSaved"
    ></TebaEmailDialog>
    <ReferralPopupDialogue
      v-model="showReferralPopupDialog"
      @input="handleReferralDialogChange"
    />
    <TestatorAssistanceGuide />
  </v-container>
</template>

<script>
  import { mdiAlertCircleOutline, mdiClose } from '@mdi/js';
  import { TESTATOR_QUERY } from '../../constants/graphql/testators';
  import { TESTATOR_STEPS_QUERY } from '../../constants/graphql/overview';
  import { HOME_QUERY } from '../../constants/graphql/home';
  import OverviewCard from './OverviewCard.vue';
  import OverviewProgressBars from './OverviewProgressBars.vue';
  import OverviewWillVerification from './OverviewWillVerification.vue';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';
  import PreExistingWillForm from './PreExistingWillForm.vue';
  import { getRelations } from '../../constants/js/people';
  import Steps from '../../helpers/steps';
  import Revise from '../../helpers/reviseStep';
  import AdvisorCodeDialog from './AdvisorCodeDialog.vue';
  import TebaEmailDialog from './TebaEmailDialog.vue';
  import ReferralPopupDialogue from '../referrals/ReferralPopupDialogue.vue';
  import LeftPanelContainer from './left_panel/LeftPanelContainer.vue';
  import TestatorAssistanceGuide from '../common/dialogs/TestatorAssistanceGuide.vue';

  export default {
    name: 'OverviewPage',
    components: {
      OverviewCard,
      OverviewProgressBars,
      OverviewWillVerification,
      SuccessSnackbar,
      PreExistingWillForm,
      AdvisorCodeDialog,
      TebaEmailDialog,
      ReferralPopupDialogue,
      LeftPanelContainer,
      TestatorAssistanceGuide,
    },
    props: {
      insuranceAlertMessage: {
        default: null,
        type: String,
      },
    },
    data() {
      return {
        icons: {
          mdiAlertCircleOutline: mdiAlertCircleOutline,
          mdiClose: mdiClose,
        },
        showUpdatesBanner: false,
        testator: {},
        testatorSteps: {},
        loading: true,
        stepsLoading: true,
        stepsNowComplete: false,
        willDetailsChanged: false,
        previewWillDetailsChanged: false,
        showAlert: false,
        insuranceAlert: false,
        alertMessage: null,
        enabledFeatures: [],
        showPreExistingWillForm: false,
        peopleRelations: {},
        home: {},
        steps: [],
        showAdvisorCodeModal: false,
        showTebaEmailModal: false,
        showReferralPopupDialog: false,
      };
    },
    watch: {
      /*
    We need to keep watches on everything that affects the steps.
    If we add the steps as a computed property, it doesn't update
    at the correct times.
    */
      testator() {
        this.showUpdatesBanner = Revise.anyStepsToRevise(this.testator);
        this.steps = Steps.getCalculatedSteps(
          this.testator,
          this.testatorSteps,
          this.testator.enabledFeatures,
          this.whitelabel.images
        );
        this.checkReferralDialogVisibility();
      },
      testatorSteps() {
        this.steps = Steps.getCalculatedSteps(
          this.testator,
          this.testatorSteps,
          this.testator.enabledFeatures,
          this.whitelabel.images
        );
      },
      enabledFeatures() {
        this.steps = Steps.getCalculatedSteps(
          this.testator,
          this.testatorSteps,
          this.enabledFeatures,
          this.whitelabel.images
        );
      },
    },
    computed: {
      allStepsCompleted() {
        return (
          this.numberOfSteps > 0 &&
          this.numberOfCompletedSteps === this.numberOfSteps
        );
      },
      remainderOfEstateChanged() {
        return Revise.remainderOfEstateChanged(this.testator);
      },
      reviseBannerTitle() {
        return this.remainderOfEstateChanged
          ? 'Your Remainder of estate needs a review'
          : 'We have made some updates';
      },
      reviseBannerText() {
        return this.remainderOfEstateChanged
          ? 'A change to your details removed someone or something from the way your remainder of estate is shared. Please revisit the Remainder of estate step and confirm how it should be divided. Your Will cannot be downloaded until this is done.'
          : 'Revisit highlighted steps and make your Will even more comprehensive.';
      },
      isTestator() {
        return this.testator.user.role == 'TESTATOR';
      },
      numberOfCompletedSteps() {
        return this.steps.filter(s => s.completed == true).length;
      },
      referralStepObject() {
        return {
          stepNumber: 0,
          forceStepDescription: 'REFERRALS',
          forceButtonText: 'Refer',
          id: 'referrals',
          title: 'Refer your friends',
          text: 'Help others to protect their legacy.',
          image: '',
          estimatedTime: '',
          redirectLocation: '/referrals',
          editLocation: '/referrals',
          show: true,
          completed: true,
          hideCompletedBadge: true,
          revise: false,
          style: 'secondary',
        };
      },
      coBrandedLogoUrl() {
        return this.whitelabel.coBranded?.logo
          ? require('@/assets/' + this.whitelabel.coBranded.logo)
          : null;
      },
      // showAdvisorCodeModal() {
      //   // Use nextTick to ensure reactivity and DOM updates are handled

      //     return (
      //     !this.advisorCodePresent &&
      //     !this.hasSeenCodeSinceLogin &&
      //     this.isAdvisor
      //   );
      // },
      referAFriendEnalbed() {
        return this.testator?.company?.referAFriend;
      },
      advisorCodePresent() {
        return !!this.testator?.user?.advisorCode;
      },
      hasSeenCodeSinceLogin() {
        return this.$store.getters.hasSeenPopupSinceLogin === true ||
          this.$store.getters.hasSeenPopupSinceLogin === 'true'
          ? true
          : false;
      },
      isMigratedFromTeba() {
        return !!this.testator?.user?.migratedFromTeba;
      },
      emailPresent() {
        return !!this.testator?.email;
      },
      hasSeenTebaEmailPopupSinceLogin() {
        return this.$store.getters.hasSeenTebaEmailPopupSinceLogin === true ||
          this.$store.getters.hasSeenTebaEmailPopupSinceLogin === 'true'
          ? true
          : false;
      },
      isAdvisor() {
        return this.$store.getters.isAdvisor;
      },
      isGBWill() {
        return this.testator.willCountry == 'GB';
      },
      numberOfSteps() {
        return this.steps.length;
      },
      numberOfStepsCompleted() {
        return this.steps.filter(s => s.completed == true).length;
      },
      calcInsuranceMargin() {
        return this.showAlert ? 70 + 'px' : 0;
      },
      step6Title() {
        return this.testator.willCountry == 'ZA'
          ? 'Living Will & Letter of wishes'
          : 'Letter of wishes';
      },
      hasOngoingCompetition() {
        return this.home?.competition?.competitionActive;
      },
      willReadyCompetitionMessage() {
        return this.home.competition?.willReadyMessage;
      },
      isMobile() {
        return this.$isMobileDevice();
      },
      determineLeftPanelContainer() {
        if (!this.isMobile) {
          return true;
        } else if (this.isMobile) {
          return this.allStepsCompleted;
        }
      },
    },
    mounted() {
      if (!!this.insuranceAlertMessage) {
        this.insuranceAlert = true;
      }

      this.checkForRedirect();
    },
    methods: {
      checkForRedirect() {
        if (
          this.$store.getters.isLoggedIn &&
          this.$route.query.redirect === 'referrals'
        ) {
          this.$router.push('/referrals');
        }
      },
      determineAdvisorCodeModal() {
        setTimeout(() => {
          this.showAdvisorCodeModal =
            !this.advisorCodePresent &&
            !this.hasSeenCodeSinceLogin &&
            this.isAdvisor;
        }, 500);
      },
      advisorCodeModalClosed() {
        this.showAdvisorCodeModal = false;
        this.$store.dispatch('setHasSeenPopupSinceLogin', true);
      },
      determineTebaEmailModal() {
        setTimeout(() => {
          this.showTebaEmailModal =
            this.isMigratedFromTeba &&
            !this.emailPresent &&
            !this.hasSeenTebaEmailPopupSinceLogin;
        }, 500);
      },
      tebaEmailModalClosed() {
        this.showTebaEmailModal = false;
        this.$store.dispatch('setHasSeenTebaEmailPopupSinceLogin', true);
      },
      tebaEmailSaved() {
        this.showTebaEmailModal = false;
        this.$store.dispatch('setHasSeenTebaEmailPopupSinceLogin', true);
        this.refetchTestator();
      },
      checkIfStepsCompleted() {
        const prevNumberOfCompletedSteps =
          this.$store.getters.numberOfCompletedSteps;
        const allStepsComplete =
          this.numberOfStepsCompleted === this.steps.length;
        const previouslyIncomplete =
          this.numberOfSteps > prevNumberOfCompletedSteps &&
          prevNumberOfCompletedSteps > 0;
        if (previouslyIncomplete && allStepsComplete) {
          this.stepsNowComplete = true;
        }
      },
      closeReviseBanner() {
        this.showUpdatesBanner = false;
      },
      updateStore() {
        this.$store.dispatch(
          'updateCompletedSteps',
          this.numberOfStepsCompleted
        );
      },
      displayAlert() {
        if (Revise.anyStepsToRevise(this.testator)) {
          return;
        }
        let text = '';
        if (this.hasOngoingCompetition) {
          text = this.willReadyCompetitionMessage;
        }
        if (this.stepsNowComplete && !!text) {
          this.alertMessage = text;
          this.showAlert = true;
        }
      },
      previewWillDownloaded() {
        this.previewWillDetailsChanged = false;
      },
      willDownloaded() {
        this.willDetailsChanged = false;
        this.$apollo.queries.testator.refetch();
      },
      handleReferralDialogChange(value) {
        if (!value) {
          localStorage.setItem('referralPopupDismissed', 'true');
          this.evaluateAssistanceGuide();
        }
      },
      evaluateAssistanceGuide() {
        if (this.loading || this.stepsLoading) return;
        if (this.showReferralPopupDialog) return;
        this.$store.dispatch('evaluateGuideRules', {
          testator: this.testator,
          steps: this.testatorSteps,
          page: 'overview',
          allStepsCompleted: this.allStepsCompleted,
          willDetailsChanged: this.willDetailsChanged,
        });
      },
      checkReferralDialogVisibility() {
        const hasBeenDismissed = localStorage.getItem('referralPopupDismissed');
        if (hasBeenDismissed === 'true') {
          this.showReferralPopupDialog = false;
          return;
        }
        const hasWillVersion = !!(
          this.testator.willVersions && this.testator.willVersions.length
        );
        this.showReferralPopupDialog =
          this.referAFriendEnalbed &&
          this.allStepsCompleted &&
          this.isTestator &&
          hasWillVersion;
      },
      refetchTestator() {
        if (this.$apollo.queries && this.$apollo.queries.testator) {
          // Force a network-only refetch to ensure fresh data
          this.$apollo.queries.testator
            .refetch()
            .then(() => {
              // Force a reactive update after refetch completes
              this.$forceUpdate();
            })
            .catch(error => {
              console.error('Error refetching testator data:', error);
            });
        }
      },
    },
    apollo: {
      testator: {
        query: TESTATOR_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        fetchPolicy: 'cache-and-network',
        result(response) {
          this.determineAdvisorCodeModal();
          this.determineTebaEmailModal();
          if (response.data && response.data.testator) {
            this.loading = false;
            this.willDetailsChanged =
              response.data.testator.willDetailsChanged ||
              this.willDetailsChanged;
            this.peopleRelations = getRelations(this.testator.people);
            this.checkReferralDialogVisibility();
            this.evaluateAssistanceGuide();
          }
        },
      },
      testatorSteps: {
        query: TESTATOR_STEPS_QUERY,
        variables() {
          return {
            id: this.testatorId,
          };
        },
        fetchPolicy: 'network-only',
        update: data => data.testator.steps,
        result(response) {
          if (response.data) {
            setTimeout(() => {
              this.enabledFeatures = response.data.testator.enabledFeatures;
              this.willDetailsChanged =
                response.data.testator.willDetailsChanged ||
                this.willDetailsChanged;
              this.previewWillDetailsChanged =
                response.data.testator.previewWillDetailsChanged ||
                this.previewWillDetailsChanged;
              this.stepsLoading = false;
              this.checkIfStepsCompleted();
              this.updateStore();
              this.displayAlert();
              this.checkReferralDialogVisibility();
              this.evaluateAssistanceGuide();
            }, 100);
          }
        },
      },
      home: {
        query: HOME_QUERY,
        fetchPolicy: 'cache-and-network',
      },
    },
  };
</script>

<style lang="scss" scoped>
  .overview-container {
    margin-bottom: 88px;
  }

  .revise-banner {
    .revise-banner-col {
      padding-left: 16px;
    }
    .title {
      font-family: Montserrat, Gill Sans, Gill Sans MT, Calibri, Trebuchet MS,
        sans-serif !important;
    }

    .col {
      div.content {
        background-color: #ff9800;
        border-radius: 8px;
        padding: 16px;
        display: flex;
        .left {
          padding-right: 8px;
        }
        .center {
          flex-grow: 1;
          .title {
            font-size: 16px;
            color: white;
            font-weight: 600;
            line-height: 20px;
          }
          .text {
            font-weight: 400;
            font-size: 14px;
            color: white;
            line-height: 18px;
            a {
              font-size: 14px;
              font-weight: 400;
              line-height: 18px;
              text-decoration: underline;
              color: #ffffff;
            }
          }
        }

        div:last-of-type {
          line-height: 18px;
        }
      }
    }
  }

  @media screen and (max-width: 599px) and (min-width: 0px) {
    .revise-banner {
      margin-top: 10px;
      .revise-banner-col {
        padding-left: 16px;
        padding-right: 16px;

        div.content {
          .center {
            .title {
              font-size: 14px !important;
            }
            .text {
              font-size: 12px !important;
              a {
                font-size: 12px !important;
              }
            }
          }
        }
      }
    }
  }
  .sticky {
    top: 15px;
    z-index: 2;
  }
  .welcome-heading {
    line-height: 38px;
  }

  .welcome-heading-container {
    h1 {
      display: inline;
    }
    .partnership-title {
      color: #009677;
    }
  }

  .co-branded-logo-container {
    width: 100%;
    padding-top: 12px;
    margin-bottom: 36px;

    img {
      max-width: 280px;
    }
  }

  .steps-title-container {
    margin-left: -60px;
  }
  .steps-title-container-mobile {
    margin-left: 16px;
    margin-top: 30px;
  }

  .steps-title {
    font-weight: 600;
    font-size: 24px;
    line-height: 120%;
    letter-spacing: 0px;
    color: #282828;
  }

  .steps-description {
    font-weight: 500;
    font-size: 18px;
    line-height: 30px;
    letter-spacing: 0px;
    color: #282828;
  }
</style>
